/**
 * File classification (brief 5.2 step 2): microdata vs area-level
 * vs area-covariates, from table structure and content. Every call
 * carries its evidence — the signals that fired and what they found
 * — so the UI can show why, and ask when the evidence is thin.
 *
 * The decisive structural fact is rows per area: microdata repeats
 * each area across many respondent rows; area-level files carry
 * exactly one row per area. Content signals (a weight column, an
 * estimate/variance/n column set) confirm the call.
 */
const AREA_NAME_RE = /area|county|district|division|region|lea|fips|site|geo|school/i;
const WEIGHT_NAME_RE = /weight|wt|wgt|finalwt/i;
const ESTIMATE_NAME_RE = /est|pct|percent|proportion|rate|direct/i;
const VARIANCE_NAME_RE = /^var|variance|se$|stderr|std.?err|moe/i;
const N_NAME_RE = /^n$|sample|respondents|_n$|count of/i;
function norm(name) {
    return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}
/**
 * The most plausible area-identifier column, from profile alone.
 * Uniqueness is NOT a disqualifier here: in an area-level file the
 * identifier is unique per row by construction. Area-named columns
 * win; among equals, the column with more distinct values wins;
 * ties keep the earlier column.
 */
function areaCandidate(profile) {
    let best = null;
    let bestNamed = false;
    for (const col of profile.columns) {
        if (col.missingShare > 0.05)
            continue;
        if (col.distinctCount < 2)
            continue;
        const named = AREA_NAME_RE.test(col.name) || AREA_NAME_RE.test(col.label ?? '');
        const plausible = col.type === 'string' || (col.numeric?.integerOnly ?? false);
        if (!plausible)
            continue;
        // A column that is unique per row and carries no area name is
        // indistinguishable from a record identifier: not a candidate.
        if (!named && col.uniqueness >= 1)
            continue;
        const cand = {
            name: col.name,
            distinct: col.distinctCount,
            rowsPerValue: profile.rowCount / col.distinctCount,
        };
        if (best === null ||
            (named && !bestNamed) ||
            (named === bestNamed && cand.distinct > best.distinct)) {
            best = cand;
            bestNamed = named;
        }
    }
    return best;
}
export function classifyTable(table, profile) {
    const evidence = [];
    const rows = profile.rowCount;
    const area = areaCandidate(profile);
    let micro = 0;
    let areaLevel = 0;
    let covariatesOnly = 0;
    // ---- Content signals (computed first: the structural verdict
    // between area-level and area-covariates depends on them). -------
    const byName = (re) => profile.columns.filter((c) => re.test(norm(c.name)) || re.test((c.label ?? '').toLowerCase()));
    const weightCols = byName(WEIGHT_NAME_RE).filter((c) => c.numeric !== null && c.numeric.min > 0);
    const estimateCols = byName(ESTIMATE_NAME_RE).filter((c) => {
        if (c.numeric === null)
            return false;
        const { min, max } = c.numeric;
        return min >= 0 && (max <= 1.0001 || max <= 100.01);
    });
    const varianceCols = byName(VARIANCE_NAME_RE).filter((c) => c.numeric !== null && c.numeric.min >= 0);
    const nCols = byName(N_NAME_RE).filter((c) => c.numeric !== null && c.numeric.integerOnly && c.numeric.min >= 0);
    const hasEstimateSet = estimateCols.length > 0 && (varianceCols.length > 0 || nCols.length > 0);
    // ---- Structural signal: rows per area value. --------------------
    if (area !== null) {
        if (area.rowsPerValue >= 3) {
            // Repetition depth is the structural signature of microdata:
            // ten or more rows per area value is decisive on its own.
            micro += area.rowsPerValue >= 10 ? 0.55 : 0.45;
            evidence.push({
                signal: 'rows-per-area',
                finding: `column "${area.name}" has ${area.distinct} distinct values over ${rows} rows (about ${area.rowsPerValue.toFixed(1)} rows per value): areas repeat across rows, as in respondent-level data`,
                pointsTo: 'microdata',
            });
        }
        else if (area.rowsPerValue <= 1.0001 && hasEstimateSet) {
            areaLevel += 0.45;
            evidence.push({
                signal: 'rows-per-area',
                finding: `column "${area.name}" has one row per value (${area.distinct} values, ${rows} rows): one row per area`,
                pointsTo: 'area-level',
            });
        }
        else if (area.rowsPerValue <= 1.0001) {
            // One row per area but nothing that reads as a published
            // estimate: an area companion file, not a results table.
            covariatesOnly += 0.45;
            evidence.push({
                signal: 'rows-per-area',
                finding: `column "${area.name}" has one row per value (${area.distinct} values, ${rows} rows), and no column set reads as a published estimate with its precision: an area companion file`,
                pointsTo: 'area-covariates',
            });
        }
        else {
            evidence.push({
                signal: 'rows-per-area',
                finding: `column "${area.name}" averages ${area.rowsPerValue.toFixed(1)} rows per value: between one-row-per-area and respondent-level`,
                pointsTo: null,
            });
        }
    }
    else {
        evidence.push({
            signal: 'rows-per-area',
            finding: 'no plausible area-identifier column was found',
            pointsTo: null,
        });
    }
    // ---- Content verdicts. ------------------------------------------
    if (weightCols.length > 0) {
        micro += 0.25;
        evidence.push({
            signal: 'weight-column',
            finding: `column "${weightCols[0].name}" looks like a survey weight (positive numeric, weight-like name)`,
            pointsTo: 'microdata',
        });
    }
    if (hasEstimateSet) {
        areaLevel += 0.3;
        evidence.push({
            signal: 'estimate-set',
            finding: `columns "${estimateCols[0].name}"${varianceCols.length > 0 ? ` with "${varianceCols[0].name}"` : ''}${nCols.length > 0 ? ` and "${nCols[0].name}"` : ''} look like a published estimate with its precision and sample size`,
            pointsTo: 'area-level',
        });
    }
    else if (area !== null && area.rowsPerValue <= 1.0001) {
        const numericCount = profile.columns.filter((c) => c.type === 'number').length;
        if (numericCount >= 1) {
            covariatesOnly += 0.25;
            evidence.push({
                signal: 'covariates-only',
                finding: 'one row per area with numeric columns but no estimate/variance/sample-size set: an area covariate file',
                pointsTo: 'area-covariates',
            });
        }
    }
    if (rows >= 500 && area !== null && area.rowsPerValue >= 3) {
        micro += 0.1;
        evidence.push({
            signal: 'row-count',
            finding: `${rows} rows is respondent-scale for ${area.distinct} areas`,
            pointsTo: 'microdata',
        });
    }
    if (rows <= 200 && area !== null && area.rowsPerValue <= 1.0001) {
        areaLevel += 0.05;
        evidence.push({
            signal: 'row-count',
            finding: `${rows} rows, one per area, is area-table scale`,
            pointsTo: 'area-level',
        });
    }
    // ---- Verdict. -----------------------------------------------------
    const scores = [
        ['microdata', micro],
        ['area-level', areaLevel],
        ['area-covariates', covariatesOnly],
    ];
    scores.sort((a, b) => b[1] - a[1]);
    const [topKind, topScore] = scores[0];
    const confidence = Math.min(1, Math.max(0, topScore));
    if (confidence < 0.5 || topScore <= scores[1][1]) {
        return { kind: 'undetermined', confidence, evidence };
    }
    return { kind: topKind, confidence, evidence };
}
//# sourceMappingURL=classify.js.map